import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import type { LoginResponse, User } from '@sss/sdk';
import { sdk } from '../../lib/sdk';
import { setSession } from '../../store/authSlice';

/**
 * Email/password sign-in with an inline two-factor step. Stores the session
 * and calls `onSignedIn` — the caller decides where to go next (the login
 * page navigates home; the OAuth consent page stays put).
 */
export default function LoginForm({ onSignedIn }: { onSignedIn: (user: User) => void }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [verifyMfaToken, setVerifyMfaToken] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const finish = (result: LoginResponse) => {
    if ('verifyMfa' in result.tokens) {
      setVerifyMfaToken(result.tokens.verifyMfa.token);
      return;
    }
    dispatch(setSession({ user: result.user, accessToken: result.tokens.access.token, refreshToken: result.tokens.refresh.token }));
    onSignedIn(result.user);
  };

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      finish(verifyMfaToken ? await sdk.auth.verifyMfa(verifyMfaToken, code) : await sdk.auth.login(email, password));
    } catch (err) {
      setError(err instanceof Error ? err.message : t('auth.loginFailed'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={onSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
      {verifyMfaToken ? (
        <label style={label}>
          {t('settings.security.mfa.mfaToken')}
          <input inputMode="numeric" autoComplete="one-time-code" maxLength={6} value={code} onChange={(e) => setCode(e.target.value)} required autoFocus />
        </label>
      ) : (
        <>
          <label style={label}>
            {t('auth.email')}
            <input type="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
          </label>
          <label style={label}>
            {t('auth.password')}
            <input type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} required />
          </label>
        </>
      )}
      <button type="submit" disabled={busy}>
        {busy ? t('common.loading') : verifyMfaToken ? t('settings.security.mfa.verify') : t('auth.signIn')}
      </button>
      {error && <p style={{ color: 'red', margin: 0 }}>{error}</p>}
    </form>
  );
}

const label: React.CSSProperties = { display: 'flex', flexDirection: 'column', gap: '0.25rem' };
